import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, RotateCcw } from "lucide-react";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <Helmet>
        <title>Refund Policy - Descriptive Test | EMRS KVS NVS Exam Preparation</title>
        <meta name="description" content="Refund and cancellation policy for Descriptive Test platform. Learn about our refund process for Pro membership and exam preparation subscriptions." />
        <link rel="canonical" href="https://descriptivetest.com/refund-policy" />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <RotateCcw className="w-6 h-6" />
              Refund Policy
            </CardTitle>
            <p className="text-orange-100 text-sm mt-1">Last updated: February 2025</p>
          </CardHeader>
          <CardContent className="p-6 md:p-8 space-y-6 text-gray-700 leading-relaxed">
            
            <p>
              This refund and cancellation policy outlines how you can cancel or seek a refund for a product/service that you have purchased through the Platform. Under this policy:
            </p>

            <div className="space-y-4">
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                <h3 className="font-semibold text-orange-800 mb-2">Cancellation Policy</h3>
                <p className="text-gray-700">
                  Cancellations will only be considered if the request is made <strong>within 2 days</strong> of placing the order. However, cancellation requests may not be entertained if the orders have been communicated to such sellers/merchant(s) listed on the Platform and they have initiated the process of shipping them, or the product is out for delivery. In such an event, you may choose to reject the product at the doorstep.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4">
                <h3 className="font-semibold text-red-800 mb-2">Non-Cancellable Items</h3>
                <p className="text-gray-700">
                  Descriptive Test does not accept cancellation requests for perishable items like flowers, eatables, etc. However, the refund/replacement can be made if the user establishes that the quality of the product delivered is not good.
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">Damaged or Defective Items</h3>
                <p className="text-gray-700">
                  In case of receipt of damaged or defective items, please report to our customer service team. The request would be entertained once the seller/merchant listed on the Platform has checked and determined the same at its own end. This should be reported <strong>within 2 days</strong> of receipt of products.
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Product Not as Expected</h3>
                <p className="text-gray-700">
                  In case you feel that the product received is not as shown on the site or as per your expectations, you must bring it to the notice of our customer service <strong>within 2 days</strong> of receiving the product. The customer service team after looking into your complaint will take an appropriate decision.
                </p>
              </div>

              <div className="bg-gray-50 border-l-4 border-gray-500 p-4">
                <h3 className="font-semibold text-gray-800 mb-2">Warranty Products</h3>
                <p className="text-gray-700">
                  In case of complaints regarding the products that come with a warranty from the manufacturers, please refer the issue to them directly.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <h3 className="font-semibold text-green-800 mb-2">Refund Processing Time</h3>
                <p className="text-gray-700">
                  In case of any refunds approved by Descriptive Test, it will take <strong>7 days</strong> for the refund to be processed to you.
                </p>
              </div>
            </div>

            {/* Summary Table */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300 text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left">Scenario</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Cancellation Request</td>
                      <td className="border border-gray-300 px-4 py-2">Within 2 days of order</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">Report Damaged/Defective Items</td>
                      <td className="border border-gray-300 px-4 py-2">Within 2 days of receipt</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Product Not as Expected</td>
                      <td className="border border-gray-300 px-4 py-2">Within 2 days of receipt</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2">Refund Processing</td>
                      <td className="border border-gray-300 px-4 py-2">7 days after approval</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Navigation to other policies */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Related Policies:</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/terms">
                  <Button variant="outline" size="sm">Terms & Conditions</Button>
                </Link>
                <Link to="/privacy-policy">
                  <Button variant="outline" size="sm">Privacy Policy</Button>
                </Link>
              </div>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
